import { ToastNotification as CarbonToastNotification } from '@carbon/react';
import type { ComponentProps } from 'react';
import { cx } from '../../utils';
import styles from './CdToast.module.scss';

type CarbonToastNotificationProps = ComponentProps<typeof CarbonToastNotification>;

export type CdToastKind = 'info' | 'success' | 'warning' | 'error';

export interface CdToastProps extends Omit<
  CarbonToastNotificationProps,
  'kind' | 'lowContrast' | 'subtitle' | 'onClose' | 'role' | 'aria-label' | 'children'
> {
  kind: CdToastKind;
  title: string;
  message?: string;
  /** Shown under the message, e.g. the time the toast was raised. */
  caption?: string;
  /** Auto-close delay in ms; 0 keeps the toast open until it is closed. */
  timeout?: number;
  /** Called on close-button click and on timeout. The caller removes the toast. */
  onDismiss: () => void;
  closeLabel?: string;
}

/** Low-contrast Carbon toast (Figma "Notification - Toast"). Errors and warnings are announced assertively. */
export const CdToast = ({
  kind,
  title,
  message,
  caption,
  timeout = 0,
  onDismiss,
  closeLabel = 'Close notification',
  className,
  ...rest
}: CdToastProps) => {
  return (
    <CarbonToastNotification
      kind={kind}
      lowContrast
      role={kind === 'error' || kind === 'warning' ? 'alert' : 'status'}
      title={title}
      subtitle={message}
      caption={caption}
      timeout={timeout}
      aria-label={closeLabel}
      // The caller owns the list, so Carbon's internal "closed" state is never used.
      onClose={() => {
        onDismiss();
        return false;
      }}
      className={cx(styles.toast, className)}
      {...rest}
    />
  );
};

export interface CdToastItem {
  id: string;
  kind: CdToastKind;
  title: string;
  message?: string;
  caption?: string;
  timeout?: number;
}

export interface CdToastStackProps {
  /** Newest first. */
  toasts: CdToastItem[];
  onDismiss: (id: string) => void;
  labels?: { close?: string };
  className?: string;
}

/** Fixed toast region: top-right from md up, full width with gutters on mobile. */
export const CdToastStack = ({ toasts, onDismiss, labels, className }: CdToastStackProps) => {
  if (toasts.length === 0) return null;
  return (
    <div className={cx(styles.stack, className)}>
      {toasts.map(({ id, ...toast }) => (
        <CdToast key={id} {...toast} onDismiss={() => onDismiss(id)} closeLabel={labels?.close} />
      ))}
    </div>
  );
};
